import { getApiBase } from '../config/api.config';
import type {
  ChangePasswordPayload,
  FetchCurrentAdminResult,
  LoginResult,
  StoredAdmin,
} from '../types/admin.types';
import {
  AUTH_ADMIN_KEY,
  AUTH_TOKEN_KEY,
  clearLocalSession,
  getAccessToken,
} from '../lib/session';
import { apiFetch } from './client';

function extractErrorMessage(data: Record<string, unknown>): string {
  const msg = data.message;
  if (typeof msg === 'string') return msg;
  if (Array.isArray(msg)) return msg.filter((x) => typeof x === 'string').join(', ');
  const errors = data.errors;
  if (errors && typeof errors === 'object') {
    const flat = Object.values(errors as Record<string, unknown>).flatMap((v) =>
      Array.isArray(v) ? v : [v],
    );
    const first = flat.find((x) => typeof x === 'string') as string | undefined;
    if (first) return first;
  }
  return '';
}

/**
 * POST /platform/login
 * Response: { success, message, data: { token, user: { id, name, email, status } } }
 */
export async function loginAdmin(email: string, password: string): Promise<LoginResult> {
  const base = getApiBase();
  let res: Response;
  try {
    res = await fetch(`${base}/platform/login`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
    });
  } catch {
    return { ok: false, message: 'تعذر الاتصال بالخادم. تحقق من الاتصال بالإنترنت.' };
  }

  let body: Record<string, unknown> = {};
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  if (!res.ok || body.success === false) {
    const msg = extractErrorMessage(body) || `HTTP ${res.status}`;
    return { ok: false, message: msg };
  }

  const envelope = body.data as Record<string, unknown> | undefined;

  const token =
    (typeof body.token === 'string' && body.token) ||
    (typeof body.access_token === 'string' && body.access_token) ||
    (envelope && typeof envelope.token === 'string' && envelope.token) ||
    (envelope && typeof envelope.access_token === 'string' && envelope.access_token) ||
    null;

  if (!token) {
    return { ok: false, message: extractErrorMessage(body) || 'Invalid response: no token' };
  }

  localStorage.setItem(AUTH_TOKEN_KEY, token);

  const profile =
    (envelope?.user as Record<string, unknown> | undefined) ??
    (envelope?.admin as Record<string, unknown> | undefined) ??
    (body.user as Record<string, unknown> | undefined) ??
    (body.admin as Record<string, unknown> | undefined);

  if (profile && typeof profile === 'object') {
    localStorage.setItem(AUTH_ADMIN_KEY, JSON.stringify(profile));
  } else {
    localStorage.removeItem(AUTH_ADMIN_KEY);
  }

  return { ok: true };
}

/**
 * POST /platform/logout
 */
export async function logout(): Promise<void> {
  const token = getAccessToken();
  if (token) {
    try {
      await fetch(`${getApiBase()}/platform/logout`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
    } catch {
      /* network failure — still clear client session */
    }
  }
  clearLocalSession();
}

/**
 * GET /platform/me
 * Response: { success, data: { user: { id, name, email, status } } }
 */
export async function fetchCurrentAdmin(): Promise<FetchCurrentAdminResult> {
  const token = getAccessToken();
  if (!token) {
    return { ok: false, unauthorized: true };
  }

  let res: Response;
  try {
    res = await apiFetch('/platform/me', { method: 'GET' });
  } catch {
    return { ok: false };
  }

  let body: Record<string, unknown> = {};
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  if (res.status === 401 || res.status === 403) {
    clearLocalSession();
    return { ok: false, unauthorized: true };
  }

  if (!res.ok) {
    return { ok: false };
  }

  const envelope = body.data as Record<string, unknown> | undefined;

  const adminRaw =
    (envelope?.user as Record<string, unknown> | undefined) ??
    (envelope?.admin as Record<string, unknown> | undefined) ??
    (body.user as Record<string, unknown> | undefined) ??
    (body.admin as Record<string, unknown> | undefined);

  if (!adminRaw || typeof adminRaw !== 'object') {
    return { ok: false };
  }

  const admin = adminRaw as StoredAdmin;
  localStorage.setItem(AUTH_ADMIN_KEY, JSON.stringify(admin));
  return { ok: true, admin };
}

/**
 * POST /platform/change-password
 */
export async function changeAdminPassword(
  payload: ChangePasswordPayload,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const token = getAccessToken();
  if (!token) {
    return { ok: false, message: '', unauthorized: true };
  }

  const base = getApiBase();
  const res = await fetch(`${base}/platform/change-password`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  let data: Record<string, unknown> = {};
  try {
    const text = await res.text();
    if (text) data = JSON.parse(text) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  if (res.status === 401 || res.status === 403) {
    clearLocalSession();
    return {
      ok: false,
      message: extractErrorMessage(data) || `HTTP ${res.status}`,
      unauthorized: true,
    };
  }

  if (!res.ok || data.success === false) {
    return { ok: false, message: extractErrorMessage(data) || `HTTP ${res.status}` };
  }

  return { ok: true };
}

/**
 * PUT /platform/me — update current admin profile
 */
export async function updateAdminProfile(payload: {
  name?: string;
  email?: string;
  phone?: string | null;
}): Promise<
  | { ok: true; admin: StoredAdmin }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const token = getAccessToken();
  if (!token) {
    return { ok: false, message: '', unauthorized: true };
  }

  const base = getApiBase();
  const res = await fetch(`${base}/platform/me`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  let body: Record<string, unknown> = {};
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  if (res.status === 401) {
    clearLocalSession();
    return { ok: false, message: extractErrorMessage(body) || 'Unauthorized', unauthorized: true };
  }

  if (!res.ok || body.success === false) {
    return { ok: false, message: extractErrorMessage(body) || `HTTP ${res.status}` };
  }

  const envelope = body.data as Record<string, unknown> | undefined;
  const adminRaw =
    (envelope?.user as Record<string, unknown> | undefined) ??
    envelope ??
    (body.user as Record<string, unknown> | undefined);

  if (!adminRaw || typeof adminRaw !== 'object') {
    return { ok: false, message: 'Invalid response' };
  }

  const admin = adminRaw as StoredAdmin;
  localStorage.setItem(AUTH_ADMIN_KEY, JSON.stringify(admin));
  return { ok: true, admin };
}

function parseAdminEnvelope(
  body: Record<string, unknown>,
): StoredAdmin | null {
  const envelope = body.data as Record<string, unknown> | undefined;
  const adminRaw =
    (envelope?.user as Record<string, unknown> | undefined) ??
    envelope ??
    (body.user as Record<string, unknown> | undefined);
  if (!adminRaw || typeof adminRaw !== 'object') return null;
  return adminRaw as StoredAdmin;
}

/**
 * POST /platform/me/avatar
 */
export async function uploadAdminAvatar(file: File): Promise<
  | { ok: true; admin: StoredAdmin }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const token = getAccessToken();
  if (!token) {
    return { ok: false, message: '', unauthorized: true };
  }

  const base = getApiBase();
  const form = new FormData();
  form.append('avatar', file);

  const res = await fetch(`${base}/platform/me/avatar`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: form,
  });

  let body: Record<string, unknown> = {};
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  if (res.status === 401) {
    clearLocalSession();
    return { ok: false, message: extractErrorMessage(body) || 'Unauthorized', unauthorized: true };
  }

  if (!res.ok || body.success === false) {
    return { ok: false, message: extractErrorMessage(body) || `HTTP ${res.status}` };
  }

  const admin = parseAdminEnvelope(body);
  if (!admin) return { ok: false, message: 'Invalid response' };
  localStorage.setItem(AUTH_ADMIN_KEY, JSON.stringify(admin));
  return { ok: true, admin };
}

/**
 * DELETE /platform/me/avatar
 */
export async function deleteAdminAvatar(): Promise<
  | { ok: true; admin: StoredAdmin }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const token = getAccessToken();
  if (!token) {
    return { ok: false, message: '', unauthorized: true };
  }

  const base = getApiBase();
  const res = await fetch(`${base}/platform/me/avatar`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  let body: Record<string, unknown> = {};
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text) as Record<string, unknown>;
  } catch {
    /* ignore */
  }

  if (res.status === 401) {
    clearLocalSession();
    return { ok: false, message: extractErrorMessage(body) || 'Unauthorized', unauthorized: true };
  }

  if (!res.ok || body.success === false) {
    return { ok: false, message: extractErrorMessage(body) || `HTTP ${res.status}` };
  }

  const admin = parseAdminEnvelope(body);
  if (!admin) return { ok: false, message: 'Invalid response' };
  localStorage.setItem(AUTH_ADMIN_KEY, JSON.stringify(admin));
  return { ok: true, admin };
}
